<template>
	<div
		class="swiper-container"
		:class="{ v2: isGridSwiperExpV2 }"
		v-observe-visibility="autoSwipeOnVisibility ? observeVisibilityOptions : undefined"
		:style="{ '--fill-duration': `${props.delay}ms` }"
		@click="stopAutoSlide"
		@touchstart="stopAutoSlide"
	>
		<div class="swiper-progress-bars">
			<div v-for="n in itemCount" class="progress-bar" :key="n">
				<div
					class="fill"
					:class="{
						'before-visible': !isVisibleOnce,
						active: currentSlideIndex + 1 === n && isAutoSlideActive,
						completed: currentSlideIndex + 1 > n,
						'completed-no-auto': currentSlideIndex + 1 === n && !isAutoSlideActive,
					}"
					@animationend="onProgressComplete"
				/>
			</div>
		</div>
		<div class="item-container" ref="itemContainer" dir="ltr">
			<div class="swiper-wrapper">
				<slot></slot>
			</div>
		</div>

		<div class="swiper-footer">
			<div v-if="showChevrons" class="swiper-footer-chevrons">
				<FontAwesomeIcon :icon="faChevronLeft" class="pointer" @click="prev" />
				<FontAwesomeIcon :icon="faChevronRight" class="pointer" @click="next" />
			</div>

			<span class="footer-details" v-if="isPromoted">{{ $t('WEBAPP_PROMOTED') }}</span>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { AppDirection } from '@/enums/app-direction';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { captureMessageForSentry } from '@/libraries/sentry';
import { useMounted, usePointerSwipe } from '@vueuse/core';
import type { SwiperInstance } from 'tiny-swiper';

import { computed, onActivated, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { useExperiment } from '@/libraries/experiment';
import { storeToRefs } from 'pinia';

let swiper: SwiperInstance | null = null;
const currentSlideIndex = ref(0);
const itemContainer = ref<HTMLElement | null>(null);
const isMounted = useMounted();
const isAutoSlideActive = ref(false);
const isVisibleOnce = ref(false);
let autoSlideSetInterval: ReturnType<typeof setInterval> | null = null;
const cssAnimationFired = ref(false);

const props = withDefaults(
	defineProps<{
		itemCount: number;
		delay: number;
		initialSlide?: number;
		persistedSlideIndex: number;
		hideControls?: boolean;
		enableViewportAutoSwipe?: boolean;
		isPromoted?: boolean;
	}>(),
	{
		initialSlide: 0,
		hideControls: false,
		enableViewportAutoSwipe: true,
		isPromoted: true,
	}
);
const emits = defineEmits(['slideChange']);
const { appDirection } = storeToRefs(useMetaStore());

const { activeVariantWithControlGroup: gridSwiperVariant } = useExperiment('EXP_GRID_SWIPER');

/**
 * Only auto swipe after swiper is in the viewport
 */
const autoSwipeOnVisibility = computed(() => props.enableViewportAutoSwipe);

const showChevrons = computed(() => {
	return !props.hideControls && !IS_TOUCH;
});

if (!IS_TOUCH) {
	const { isSwiping } = usePointerSwipe(itemContainer);
	// Ignore click events *while* swiping on desktop
	watch(isSwiping, isCurrentlySwiping => {
		if (!isCurrentlySwiping) return;

		itemContainer.value?.addEventListener(
			'click',
			e => {
				e.preventDefault();
				e.stopPropagation();
			},
			{ once: true, capture: true }
		);
	});
}

const isGridSwiperExpV2 = computed(() => gridSwiperVariant.value === 'variant_2');

const spaceBetweenSlides = computed(() => {
	return isGridSwiperExpV2.value ? 12 : 32;
});

const isRTL = computed(() => appDirection.value === AppDirection.RTL);

onMounted(() => {
	initSwiper();
	window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize);
	stopAutoSlide();
});

onActivated(initSwiper);

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

function onVisibilityChanged(isVisible: boolean) {
	if (isVisible) {
		startAutoSlide();
		isVisibleOnce.value = true;
	}
}

function next(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		prev(true);
		return;
	}

	stopAutoSlide();
	const toIndex = currentSlideIndex.value === props.itemCount - 1 ? 0 : currentSlideIndex.value + 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'right' });
}

function prev(allowRTL: boolean = false) {
	if (isRTL.value && !allowRTL) {
		next(true);
		return;
	}

	stopAutoSlide();
	const toIndex = currentSlideIndex.value === 0 ? props.itemCount - 1 : currentSlideIndex.value - 1;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'left' });
}

function changeSlideIndex({
	toIndex,
	direction,
	nonInteraction = false,
}: {
	toIndex: number;
	direction: 'left' | 'right';
	nonInteraction?: boolean;
}): void {
	const prevIndex = currentSlideIndex.value;
	currentSlideIndex.value = toIndex;

	if (prevIndex !== null && toIndex !== prevIndex && isMounted.value) {
		emits('slideChange', {
			direction: direction,
			position: toIndex,
			nonInteraction: nonInteraction,
		});
	}
}

function initSwiper(persistedSlideIndex: number = 0) {
	try {
		// swiper has a top level global variable calling `requestAnimationFrame` so the import needs to be client only
		// Because of a watcher in the tree, this function should not be async
		import('tiny-swiper').then(({ default: Swiper }) => {
			if (itemContainer.value && import.meta.client) {
				if (swiper == null) {
					swiper = new Swiper(itemContainer.value, {
						loop: false,
						spaceBetween: spaceBetweenSlides.value, // Add gap between slides
						slidesPerView: 1, // Show one full slide at a time
						resistance: true,
						resistanceRatio: 0, // Prevent partial sliding beyond boundaries
					});
				}

				swiper.on('before-slide', (current: number, state: any, next: number) => {
					// on element swipe state.isTouching === true
					if (state?.isTouching) {
						stopAutoSlide();
						const direction = current > next ? 'left' : 'right';
						changeSlideIndex({
							toIndex: next,
							direction,
							nonInteraction: false,
						});
					}
				});
			}
		});
	} catch (error: any) {
		captureMessageForSentry('[Initialize Tiny Swiper]', { error, where: '[PromotionSwiper]: initSwiper' }, 'error');
	}
}

function handleResize() {
	if (swiper?.update) {
		swiper.update();
	}
}

function advanceSlide() {
	if (!isAutoSlideActive.value) return;
	const toIndex = (currentSlideIndex.value + 1) % props.itemCount;
	swiper?.slideTo(toIndex);
	changeSlideIndex({ toIndex, direction: 'right', nonInteraction: true });
	cssAnimationFired.value = false; // Reset for next slide
}

function onProgressComplete() {
	if (!isAutoSlideActive.value) return;
	cssAnimationFired.value = true;
	clearAutoSlideInterval(); // Remove safety net interval
	advanceSlide();
}

function clearAutoSlideInterval() {
	if (autoSlideSetInterval !== null) {
		clearInterval(autoSlideSetInterval);
		autoSlideSetInterval = null;
	}
}

function startAutoSlide() {
	clearAutoSlideInterval();
	isAutoSlideActive.value = true;
	cssAnimationFired.value = false;

	// Safety net: if animation didn't fire, force advance
	autoSlideSetInterval = setInterval(() => {
		console.log('fired');
		if (!cssAnimationFired.value) {
			console.warn('[PromotionSwiper] animationend did not fire, using fallback');
			advanceSlide();
		}
	}, props.delay + 50);
}

function stopAutoSlide() {
	clearAutoSlideInterval();
	isAutoSlideActive.value = false;
}

defineExpose({
	next,
	prev,
	currentSlideIndex,
});
</script>

<style lang="scss" scoped>
.swiper-container {
	width: 100%;
	height: 100%;
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	gap: 16px;
	display: flex;
	flex-direction: column;

	&.v2 {
		padding: 0px 8px;
	}

	.swiper-progress-bars {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		z-index: 2;

		.progress-bar {
			height: 2px;
			width: 100%;
			background-color: #ffffff14;
			position: relative;
			overflow: hidden;
			border-radius: 2px;

			.fill {
				height: 100%;
				width: 0;
				background: linear-gradient(90deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 1) 100%);
				opacity: 0.3;
				transition: opacity 0.3s ease-in-out;
				position: relative;
				border-radius: 2px;

				&::after {
					content: '';
					position: absolute;
					right: 0;
					top: 50%;
					transform: translateY(-50%);
					width: 4px;
					height: 4px;
					background: #ffffff;
					border-radius: 50%;
					box-shadow: 0 0 8px rgba(132, 64, 64, 0.8);
					opacity: 0;
					transition: opacity 0.2s ease-in-out;
				}

				&.completed {
					width: 100%;
					opacity: 1;
					transition: opacity 0.3s ease-in-out, width 1.2s ease-out;
				}

				&.completed-no-auto {
					width: 100%;
					opacity: 1;
					transition: opacity 0.15s ease-in-out;
				}

				&.active {
					opacity: 1;
					animation: fillProgress linear forwards;
					animation-duration: var(--fill-duration);
					transition: opacity 0.3s ease-in-out;

					&::after {
						opacity: 1;
					}
				}

				&.before-visible {
					animation: none;
					transition: none;
					width: 0;
				}
			}
		}
	}

	@keyframes fillProgress {
		from {
			width: 0%;
		}
		to {
			width: 100%;
		}
	}

	.swiper-footer {
		position: static;
		width: 100%;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		gap: 8px;
		direction: ltr;
		z-index: 2;
		cursor: pointer;

		&-chevrons {
			display: flex;
			gap: 16px;

			@media (max-width: 640px) {
				display: none;
			}

			.is-last {
				color: grey;
			}
		}
	}
}

.swiper-wrapper {
	display: flex;
	flex-direction: row;
	:deep(.swiper-item) {
		position: absolute;
	}
}

.item-container {
	border-radius: 8px;
}

.footer-details {
	margin: 0;
	color: #d2d3d5;
	font-size: 14px;
	line-height: 16px;
	letter-spacing: 0.21px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-inline-start: auto;
}
</style>
